// src/config/reportingCards.js
// Single source of truth for reporting dashboard metrics, sections, and home badges.
// Organization-level reporting_config controls visibility/order; this registry defines rendering.

import {
  Users, UserPlus, Calendar, Clock, Activity, ClipboardCheck,
  DollarSign, TrendingUp, BarChart3, Shield, Briefcase, Share2,
  Target, CalendarClock, FileText, FileWarning, CreditCard, Building2,
  UserMinus, MessageSquare, BadgeCheck, Send, PauseCircle
} from 'lucide-react';

// --- Formatters ---------------------------------------------------------------

const formatCurrency = (val) =>
  val != null
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val)
    : '—';

const formatCurrencyDecimal = (val) =>
  val != null
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val)
    : '—';

export const FORMATTERS = {
  number: (v) => v != null ? v.toLocaleString() : '—',
  decimal1: (v) => v != null ? v.toFixed(1) : '—',
  currency: formatCurrency,
  currencyDecimal: formatCurrencyDecimal,
  hours: (v) => v != null ? `${v} hrs` : '—',
  percent: (v) => v != null ? `${v}%` : '—',
};

// --- Icon map -----------------------------------------------------------------

export const ICON_MAP = {
  Users,
  UserPlus,
  Calendar,
  Clock,
  Activity,
  ClipboardCheck,
  DollarSign,
  TrendingUp,
  BarChart3,
  Shield,
  Briefcase,
  Share2,
  Target,
  CalendarClock,
  FileText,
  FileWarning,
  CreditCard,
  Building2,
  UserMinus,
  MessageSquare,
  BadgeCheck,
  Send,
  PauseCircle,
};

// --- Card Registry ------------------------------------------------------------

export const CARD_REGISTRY = {
  // Patients
  'patients.new_patients': {
    id: 'patients.new_patients',
    section: 'patients',
    label: 'New Patients',
    icon: 'UserPlus',
    color: 'blue',
    dataPath: 'patients.new_patients',
    comparePath: ['patients', 'new_patients'],
    formatter: 'number',

  },
  'patients.new_evals': {
    id: 'patients.new_evals',
    section: 'patients',
    label: 'New Evaluations',
    icon: 'ClipboardCheck',
    color: 'cyan',
    dataPath: 'patients.new_evals',
    comparePath: ['patients', 'new_evals'],
    formatter: 'number',

    conditional: (data) => data?.patients?.new_evals != null,
  },
  'patients.visits': {
    id: 'patients.visits',
    section: 'patients',
    label: 'Visits',
    icon: 'Calendar',
    color: 'green',
    dataPath: 'patients.visits',
    comparePath: ['patients', 'visits'],
    formatter: 'number',

  },
  'patients.visits_per_patient': {
    id: 'patients.visits_per_patient',
    section: 'patients',
    label: 'Visits / Patient',
    icon: 'Activity',
    color: 'purple',
    dataPath: 'patients.visits_per_patient',
    comparePath: ['patients', 'visits_per_patient'],
    formatter: 'decimal1',
  },
  'patients.active_cases': {
    id: 'patients.active_cases',
    section: 'patients',
    label: 'Active Cases',
    icon: 'Briefcase',
    color: 'purple',
    dataPath: 'caseload_total',
    comparePath: ['patients', 'active_cases'],
    formatter: 'number',
    subtitle: 'current snapshot — not period-filtered',
    conditional: (data) => data?.caseload_total != null,
  },

  // Productivity
  'productivity.procedures': {
    id: 'productivity.procedures',
    section: 'productivity',
    label: 'Procedures (Weighted)',
    icon: 'Activity',
    color: 'cyan',
    dataPath: 'productivity.procedures',
    comparePath: ['productivity', 'procedures'],
    formatter: 'number',
  },
  'productivity.procedures_unweighted': {
    id: 'productivity.procedures_unweighted',
    section: 'productivity',
    label: 'Procedures (Unweighted)',
    icon: 'Activity',
    color: 'cyan',
    dataPath: 'productivity.procedures_unweighted',
    comparePath: ['productivity', 'procedures_unweighted'],
    formatter: 'number',
    conditional: (data) => data?.productivity?.procedures_unweighted != null,
  },
  'productivity.procedures_per_hour': {
    id: 'productivity.procedures_per_hour',
    section: 'productivity',
    label: 'Procedures / Hour',
    icon: 'TrendingUp',
    color: 'purple',
    dataPath: 'productivity.procedures_per_hour',
    comparePath: ['productivity', 'procedures_per_hour'],
    formatter: 'decimal1',
    subtitle: 'weighted',
    conditional: (data) => data?.productivity?.procedures_per_hour != null,
  },
  'productivity.procedures_per_visit': {
    id: 'productivity.procedures_per_visit',
    section: 'productivity',
    label: 'Procedures / Visit',
    icon: 'TrendingUp',
    color: 'purple',
    dataPath: 'productivity.procedures_per_visit',
    comparePath: ['productivity', 'procedures_per_visit'],
    formatter: 'decimal1',
    subtitle: 'weighted',
    conditional: (data) => data?.productivity?.procedures_per_visit != null,
  },
  'productivity.provider_work_hours': {
    id: 'productivity.provider_work_hours',
    section: 'productivity',
    label: 'Provider Work Hours',
    icon: 'Clock',
    color: 'amber',
    dataPath: 'productivity.provider_work_hours',
    comparePath: ['productivity', 'provider_work_hours'],
    formatter: 'hours',
  },
  'productivity.visits_per_8hr_day': {
    id: 'productivity.visits_per_8hr_day',
    section: 'productivity',
    label: 'Visits / 8 hr Day',
    icon: 'TrendingUp',
    color: 'green',
    dataPath: 'productivity.visits_per_8hr_day',
    comparePath: ['productivity', 'visits_per_8hr_day'],
    formatter: 'decimal1',
  },
  'productivity.visits_per_40hr_week': {
    id: 'productivity.visits_per_40hr_week',
    section: 'productivity',
    label: 'Visits / 40 hr Week',
    icon: 'TrendingUp',
    color: 'green',
    dataPath: 'productivity.visits_per_40hr_week',
    comparePath: ['productivity', 'visits_per_40hr_week'],
    formatter: 'decimal1',
  },
  'productivity.new_patients_per_40hr_week': {
    id: 'productivity.new_patients_per_40hr_week',
    section: 'productivity',
    label: 'New Patients / 40 hr Week',
    icon: 'UserPlus',
    color: 'blue',
    dataPath: 'productivity.new_patients_per_40hr_week',
    comparePath: ['productivity', 'new_patients_per_40hr_week'],
    formatter: 'decimal1',
  },
  'productivity.new_evals_per_40hr_week': {
    id: 'productivity.new_evals_per_40hr_week',
    section: 'productivity',
    label: 'New Evals / 40 hr Week',
    icon: 'ClipboardCheck',
    color: 'cyan',
    dataPath: 'productivity.new_evals_per_40hr_week',
    comparePath: ['productivity', 'new_evals_per_40hr_week'],
    formatter: 'decimal1',
    conditional: (data) => data?.productivity?.new_evals_per_40hr_week != null,
  },
  'productivity.points': {
    id: 'productivity.points',
    section: 'productivity',
    label: 'Total Points',
    icon: 'Target',
    color: 'amber',
    dataPath: 'productivity.points',
    comparePath: ['productivity', 'points'],
    formatter: 'number',
    subtitle: 'Medicare 8-min rule units (all)',
  },
  'productivity.points_outpatient': {
    id: 'productivity.points_outpatient',
    section: 'productivity',
    label: 'Outpatient Points',
    icon: 'Target',
    color: 'amber',
    dataPath: 'productivity.points_outpatient',
    comparePath: ['productivity', 'points_outpatient'],
    formatter: 'number',
    subtitle: 'CPT-based visits',
  },
  'productivity.points_home_health': {
    id: 'productivity.points_home_health',
    section: 'productivity',
    label: 'Home Health Points',
    icon: 'Target',
    color: 'cyan',
    dataPath: 'productivity.points_home_health',
    comparePath: ['productivity', 'points_home_health'],
    formatter: 'number',
    subtitle: 'duration-based visits',
  },
  'productivity.treatment_minutes_outpatient': {
    id: 'productivity.treatment_minutes_outpatient',
    section: 'productivity',
    label: 'Outpatient Treatment Minutes',
    icon: 'Clock',
    color: 'cyan',
    dataPath: 'productivity.treatment_minutes_outpatient',
    comparePath: ['productivity', 'treatment_minutes_outpatient'],
    formatter: 'number',
    subtitleFn: (d) => basisSubtitle(d, null),
  },
  'productivity.treatment_minutes_home_health': {
    id: 'productivity.treatment_minutes_home_health',
    section: 'productivity',
    label: 'Home Health Treatment Minutes',
    icon: 'Clock',
    color: 'green',
    dataPath: 'productivity.treatment_minutes_home_health',
    comparePath: ['productivity', 'treatment_minutes_home_health'],
    formatter: 'number',
    subtitleFn: (d) => basisSubtitle(d, null),
  },
  'productivity.productivity_pct': {
    id: 'productivity.productivity_pct',
    section: 'productivity',
    label: 'Productivity %',
    icon: 'Activity',
    color: 'green',
    dataPath: 'productivity.productivity_pct',
    comparePath: ['productivity', 'productivity_pct'],
    formatter: 'percent',
    subtitle: 'treatment / paid time',
    subtitleFn: (d) => basisSubtitle(d, '/ paid time'),
  },

  // Payment
  'payment.charges': {
    id: 'payment.charges',
    section: 'payment',
    label: 'Charges',
    icon: 'DollarSign',
    color: 'amber',
    dataPath: 'payment.charges',
    comparePath: ['payment', 'charges'],
    formatter: 'currency',

  },
  'payment.payments': {
    id: 'payment.payments',
    section: 'payment',
    label: 'Payments',
    icon: 'DollarSign',
    color: 'green',
    dataPath: 'payment.payments',
    comparePath: ['payment', 'payments'],
    formatter: 'currency',

  },
  'payment.payments_per_visit': {
    id: 'payment.payments_per_visit',
    section: 'payment',
    label: 'Payments / Visit',
    icon: 'TrendingUp',
    color: 'purple',
    dataPath: 'payment.payments_per_visit',
    comparePath: ['payment', 'payments_per_visit'],
    formatter: 'currencyDecimal',
  },
  'payment.payments_per_hour': {
    id: 'payment.payments_per_hour',
    section: 'payment',
    label: 'Payments / Hour',
    icon: 'TrendingUp',
    color: 'cyan',
    dataPath: 'payment.payments_per_hour',
    comparePath: ['payment', 'payments_per_hour'],
    formatter: 'currencyDecimal',
  },
  'payment.payments_per_procedure': {
    id: 'payment.payments_per_procedure',
    section: 'payment',
    label: 'Payments / Procedure',
    icon: 'TrendingUp',
    color: 'rose',
    dataPath: 'payment.payments_per_procedure',
    comparePath: ['payment', 'payments_per_procedure'],
    formatter: 'currencyDecimal',
    conditional: (data) => data?.payment?.payments_per_procedure != null,
  },
};

// Productivity basis label (dashboard `productivity_basis` + per-response fallback
// count). Absent keys (older payloads) fall back to the static wording.
// `visit_capacity`: Productivity % = completed / potential visits, so the Productivity
// card (tail given) reads "N of M potential visits"; the treatment-minute cards
// (no tail) keep their static subtitle.
const BASIS_LABEL = { billed_minutes: 'billed minutes', scheduled_duration: 'scheduled time' };
function basisSubtitle(data, tail) {
  if (data?.productivity_basis === 'visit_capacity') {
    if (!tail) return null;
    const v = data?.productivity?.visits ?? data?.patients?.visits;
    const p = data?.productivity?.potential_visits;
    return p != null ? `${v ?? 0} of ${p} potential visits` : 'completed / potential visits';
  }
  const basis = BASIS_LABEL[data?.productivity_basis];
  if (!basis) return null;
  const fb = data?.productivity?.treatment_minutes_fallback_visits;
  // settings.reporting.admin_time_productive: the endpoint echoes admin_time_productive and admin block minutes sit in the ratio
  const apTail = tail && data?.admin_time_productive === true ? `+ admin ${tail}` : tail;
  const base = apTail ? `${basis} ${apTail}` : basis;
  return fb > 0 ? `${base} · ${fb} visit${fb === 1 ? '' : 's'} used fallback` : base;
}

// --- Sections -----------------------------------------------------------------

export const SECTIONS = [
  { id: 'patients', label: 'Patients', icon: 'Users' },
  { id: 'visit_segments', label: 'Visit Breakdown', icon: 'Calendar', type: 'table' },
  { id: 'productivity', label: 'Productivity', icon: 'BarChart3' },
  { id: 'payment', label: 'Payment', icon: 'DollarSign' },
  { id: 'payer_insights', label: 'Payer Insights', icon: 'Shield', type: 'table' },
  { id: 'code_mix', label: 'Code Mix (Procedures drill-down)', icon: 'Activity', type: 'dim' },
  { id: 'visit_types', label: 'Visit Types (Visits drill-down)', icon: 'Calendar', type: 'dim' },
  { id: 'caseload', label: 'Active Caseload', icon: 'Briefcase', type: 'table' },
  { id: 'referrals', label: 'Referrals', icon: 'Share2', type: 'cards' },
];

// --- Default card order (used when reporting_config is absent) -----------------

export const DEFAULT_CARD_ORDER = {
  patients: [
    'patients.new_patients',
    'patients.new_evals',
    'patients.visits',
    'patients.visits_per_patient',
    'patients.active_cases',
  ],
  productivity: [
    'productivity.procedures',
    'productivity.procedures_unweighted',
    'productivity.procedures_per_hour',
    'productivity.procedures_per_visit',
    'productivity.provider_work_hours',
    'productivity.visits_per_8hr_day',
    'productivity.visits_per_40hr_week',
    'productivity.new_patients_per_40hr_week',
    'productivity.new_evals_per_40hr_week',
  ],
  payment: [
    'payment.charges',
    'payment.payments',
    'payment.payments_per_visit',
    'payment.payments_per_hour',
    'payment.payments_per_procedure',
  ],
};

// --- Home badge registry ------------------------------------------------------

export const HOME_BADGE_REGISTRY = {
  clinical_review: {
    id: 'clinical_review',
    label: 'Clinical Review',
    icon: 'ClipboardCheck',
    color: 'yellow',
    dataKey: 'clinical_review_pending',
    formatter: 'number',
    // Pending count of the provider's own billing-flagged visits; links to the
    // provider-scoped review page. Count is live — not affected by the period picker.
    action: '/clinical-review',
    actionLabel: 'Review',
    // Renders in the home Pending Actions grid, not the My Metrics badge grid
    section: 'pending_actions',
  },
  points: {
    id: 'points',
    label: 'Points',
    icon: 'Target',
    color: 'amber',
    dataKey: 'points',
    formatter: 'number',
  },
  productivity_pct: {
    id: 'productivity_pct',
    label: 'Productivity %',
    icon: 'Activity',
    color: 'green',
    dataKey: 'productivity_pct',
    formatter: 'percent',
  },
  note_completion_pct: {
    id: 'note_completion_pct',
    label: 'Note Completion Rate',
    icon: 'ClipboardCheck',
    color: 'green',
    dataKey: 'note_completion_pct',
    formatter: 'percent',
    // Same formula and org settings as the reporting Operations tab; short
    // periods where all visits are still inside the signing window show '—'.
  },
  visits: {
    id: 'visits',
    label: 'Visits',
    icon: 'Calendar',
    color: 'blue',
    dataKey: 'visits',
    formatter: 'number',
  },
  treatment_minutes: {
    id: 'treatment_minutes',
    label: 'Treatment Minutes',
    icon: 'Clock',
    color: 'cyan',
    dataKey: 'treatment_minutes',
    formatter: 'number',
  },
  points_outpatient: {
    id: 'points_outpatient',
    label: 'Outpatient Points',
    icon: 'Target',
    color: 'amber',
    dataKey: 'points_outpatient',
    formatter: 'number',
  },
  points_home_health: {
    id: 'points_home_health',
    label: 'Home Health Points',
    icon: 'Target',
    color: 'cyan',
    dataKey: 'points_home_health',
    formatter: 'number',
  },
  treatment_minutes_outpatient: {
    id: 'treatment_minutes_outpatient',
    label: 'Outpatient Treatment Minutes',
    icon: 'Clock',
    color: 'cyan',
    dataKey: 'treatment_minutes_outpatient',
    formatter: 'number',
  },
  treatment_minutes_home_health: {
    id: 'treatment_minutes_home_health',
    label: 'Home Health Treatment Minutes',
    icon: 'Clock',
    color: 'green',
    dataKey: 'treatment_minutes_home_health',
    formatter: 'number',
  },
  pto_points: {
    id: 'pto_points',
    label: 'PTO Points',
    icon: 'Target',
    color: 'green',
    dataKey: 'pto_points',
    formatter: 'number',
    salariedOnly: true,
  },
  admin_points: {
    id: 'admin_points',
    label: 'Admin Points',
    icon: 'Target',
    color: 'cyan',
    dataKey: 'admin_points',
    formatter: 'number',
  },
  holiday_points: {
    id: 'holiday_points',
    label: 'Holiday Points',
    icon: 'Target',
    color: 'amber',
    dataKey: 'holiday_points',
    formatter: 'number',
    salariedOnly: true,
  },
  unread_messages: {
    id: 'unread_messages',
    label: 'Unread Messages',
    icon: 'MessageSquare',
    color: 'blue',
    dataKey: 'unread_messages',
    formatter: 'number',
  },
};

// --- Operations & Revenue Cycle tab sections ---------------------------------

export const OPERATIONS_SECTIONS = [
  { id: 'ops_scheduling', label: 'Scheduling', icon: 'CalendarClock' },
  { id: 'ops_documentation', label: 'Documentation', icon: 'FileText' },
  { id: 'ops_authorizations', label: 'Authorizations & Referrals', icon: 'Shield' },
  { id: 'ops_referral_sources', label: 'Referral Sources', icon: 'Share2' },
  { id: 'ops_discharges', label: 'Discharges', icon: 'UserMinus' },
  { id: 'ops_lost_patients', label: 'Lost Patients', icon: 'Users' },
  { id: 'ops_licensing', label: 'Licensing', icon: 'BadgeCheck' },
  { id: 'ops_visit_alerts', label: 'Visit Alerts', icon: 'CalendarClock' },
  { id: 'ops_self_service', label: 'Online Self-Service', icon: 'CalendarClock' },
];

export const REVENUE_CYCLE_SECTIONS = [
  { id: 'rc_ar_analysis', label: 'A/R Analysis', icon: 'DollarSign' },
  { id: 'rc_denial_analysis', label: 'Denial Analysis', icon: 'FileWarning' },
  { id: 'rc_collection_performance', label: 'Collection Performance', icon: 'CreditCard' },
  { id: 'rc_payer_performance', label: 'Payer Performance', icon: 'Building2' },
  { id: 'rc_unbilled_analysis', label: 'Unbilled Analysis', icon: 'FileWarning' },
];

// Billing Ops tab (BillingOpsTab.jsx; org config key `billing_ops_sections`).
export const BILLING_OPS_SECTIONS = [
  { id: 'bo_posting', label: 'Posting', icon: 'Send' },
  { id: 'bo_holds', label: 'Holds', icon: 'PauseCircle' },
];

// --- Resolver helpers ---------------------------------------------------------

export function resolveVisibleCards(reportingConfig, sectionId) {
  if (!reportingConfig?.sections?.[sectionId]) {
    return DEFAULT_CARD_ORDER[sectionId] || [];
  }
  const sectionConfig = reportingConfig.sections[sectionId];
  if (sectionConfig.visible === false) return [];
  if (sectionConfig.cards) return sectionConfig.cards;
  return DEFAULT_CARD_ORDER[sectionId] || [];
}

export function isSectionVisible(reportingConfig, sectionId) {
  if (!reportingConfig?.sections?.[sectionId]) return true;
  return reportingConfig.sections[sectionId].visible !== false;
}

// Default home Pending Actions cards a role can opt OUT of (checked = hidden).
// Default stays "shown" so role-less users are unaffected — mirrors the
// hidden_menu_items idiom for the sidebar.
export const HIDEABLE_HOME_CARDS = [
  { id: 'unread_messages', label: 'Unread Messages' },
];

export function resolveHomeBadges(reportingConfig, roleConfig) {
  if (roleConfig?.home_badges?.length > 0) return roleConfig.home_badges;
  return reportingConfig?.home_badges || [];
}

export function getNestedValue(obj, dotPath) {
  if (!obj || !dotPath) return undefined;
  return dotPath.split('.').reduce((acc, key) => acc?.[key], obj);
}

export function isTabSectionVisible(reportingConfig, configKey, sectionId) {
  if (!reportingConfig?.[configKey]?.[sectionId]) return true;
  return reportingConfig[configKey][sectionId].visible !== false;
}

// --- Role-level restrictions -----------------------------------------------
// Merges a role's reporting_hidden_sections ({ tabId: sectionId[] }) into the
// org reporting_config as visible:false overrides — the intersection model:
// hidden by org OR hidden by role => hidden. Absent role keys change nothing.
export const ROLE_TAB_CONFIG_KEYS = {
  dashboard: 'sections',
  operations: 'operations_sections',
  revenue_cycle: 'revenue_cycle_sections',
  billing_ops: 'billing_ops_sections',
};

export function applyRoleRestrictions(reportingConfig, hiddenSections) {
  if (!hiddenSections || typeof hiddenSections !== 'object') return reportingConfig;
  const cfg = { ...(reportingConfig || {}) };
  Object.entries(ROLE_TAB_CONFIG_KEYS).forEach(([tabId, cfgKey]) => {
    const hidden = hiddenSections[tabId];
    if (!Array.isArray(hidden) || hidden.length === 0) return;
    const cur = { ...(cfg[cfgKey] || {}) };
    hidden.forEach(id => { cur[id] = { ...(cur[id] || {}), visible: false }; });
    cfg[cfgKey] = cur;
  });
  return cfg;
}
